import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import download from 'downloadjs';
import { toast } from '@osago/ui';

import Layout from '../components/layout';
import Icon from '../components/icon';
import Frame from '../components/frame';
import { useSessionId } from '../hooks/useSessionId';
import { Message } from '../types/models/message';

import {
  getMessageById,
  getMailAttachment,
  deleteMail,
} from '../services/messagesServices';
import { DOMAIN_NAME } from '../config/api';
import { ymEvent } from '../hooks/useYandexMetrika';
import { YandexMetrikaEvent } from '../types/metrica/YandexMetrikaEvent';

const ATTACHMENT_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  zip: 'application/zip',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

const attachmentMime = (name: string): string =>
  ATTACHMENT_MIME[name.split('.').pop()?.toLowerCase() ?? ''] ?? 'application/octet-stream';

const SingleMessagePage = () => {
  const [message, setMessage] = useState<Message | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const sessionId = useSessionId();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    if (!id) return;

    getMessageById(sessionId, id).then(([error, result]) => {
      if (error || !result?.data) {
        setLoadFailed(true);
        return;
      }

      setMessage(result.data as Message);
    });
  }, [sessionId, id]);

  const handleAttachmentClick = (name: string) => {
    if (!id) return;

    ymEvent(YandexMetrikaEvent.LK_MESSAGES_DOWNLOAD_BTN_CLICK);
    getMailAttachment(sessionId, id, name).then(([error, result]) => {
      const attachmentData: any = !error && result ? result.data || result : null;
      if (!attachmentData?.content || !attachmentData?.name) {
        toast.error('Не удалось скачать вложение. Попробуйте позже.');
        return;
      }
      const mime = attachmentMime(attachmentData.name);
      download(`data:${mime};base64,${attachmentData.content}`, attachmentData.name, mime);
    });
  };

  const handleDeleteClick = () => {
    if (!id) return;

    ymEvent(YandexMetrikaEvent.LK_MESSAGES_DELETE_BTN_CLICK);
    deleteMail(sessionId, id).then(([error]) => {
      if (!error) {
        navigate('/messages');
      }
    });
  };

  return (
    <Layout>
      <Helmet defaultTitle="Загрузка...">
        <title>{message && `Сообщение: ${message.subject} | ${DOMAIN_NAME}`}</title>
      </Helmet>
      <div className="box box--bordered">
        {message ? (
          <div className="message">
            <div className="message-actions">
              <Link to="/messages" className="message-action">
                <Icon
                  name="angle-left"
                  className="fill-current mr-2"
                  width={14}
                  height={14}
                />
                Назад
              </Link>
              <button className="message-action" onClick={handleDeleteClick}>
                Удалить
                <Icon
                  name="trash"
                  className="fill-current ml-2"
                  width={16}
                  height={16}
                />
              </button>
            </div>
            <h2 className="message-title">{message.subject}</h2>

            <div className="message-body">
              <Frame content={message.body} />
            </div>

            {message.attachments && message.attachments.length > 0 && (
              <p className="font-semibold leading-tight mb-2">Вложения:</p>
            )}
            {message.attachments && (
              <ul className="messages-attachments">
                {message.attachments.map((item, key) => {
                  const filename = /[.]/.exec(item)
                    ? /[^.]+$/.exec(item)
                    : undefined;

                  if (!filename) return null;

                  return (
                    <li
                      key={key}
                      onClick={handleAttachmentClick.bind(null, item)}
                    >
                      {item}
                      <Icon
                        name="paperclip"
                        className="fill-current ml-2"
                        width={14}
                        height={14}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ) : (
          <p className="text-center">
            {loadFailed ? 'Не удалось загрузить сообщение. Попробуйте позже.' : 'Загрузка...'}
          </p>
        )}
      </div>
    </Layout>
  );
};

export default SingleMessagePage;
